import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface CancelAppointmentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  appointmentName: string;
  appointmentPhone: string;
  scheduledDate?: Date;
  onConfirmCancel: (reason: string) => void;
}

const CancelAppointmentDialog: React.FC<CancelAppointmentDialogProps> = ({
  isOpen,
  onClose,
  appointmentName,
  appointmentPhone,
  scheduledDate,
  onConfirmCancel,
}) => {
  const [t] = useTranslation('schedule');
  const [cancelReason, setCancelReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOtherReason, setIsOtherReason] = useState(false);
  const [customReason, setCustomReason] = useState('');

  const predefinedReasons = [
    { key: 'client_request', value: t('client_requested_cancellation') || 'Solicitud del cliente' },
    { key: 'scheduling_conflict', value: t('scheduling_conflict') || 'Conflicto de horarios' },
    { key: 'payment_issues', value: t('payment_issues') || 'Problemas de pago' },
    { key: 'other', value: t('other_reason') || 'Otro motivo' },
  ];

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  };

  const handleConfirm = async () => {
    const finalReason = isOtherReason ? customReason : cancelReason;
    if (!finalReason.trim()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onConfirmCancel(finalReason);
      // Reset form
      setCancelReason('');
      setCustomReason('');
      setIsOtherReason(false);
      onClose();
    } catch (error) {
      console.error('Error canceling appointment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setCancelReason('');
    setCustomReason('');
    setIsOtherReason(false);
    onClose();
  };

  const handleReasonSelect = (reason: string) => {
    if (reason === (t('other_reason') || 'Otro motivo')) {
      setIsOtherReason(true);
      setCancelReason('');
      setCustomReason('');
    } else {
      setIsOtherReason(false);
      setCancelReason(reason);
      setCustomReason('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal modal-open" onClick={handleClose}>
      <div className="modal-box w-11/12 max-w-2xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-bold text-lg text-error mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="inline-block h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          {t('cancel_appointment') || 'Cancelar Cita'}
        </h3>
        
        {/* Appointment details */}
        <div className="bg-base-200 p-4 rounded-lg mb-6">
          <h4 className="font-semibold text-brand-heading mb-2">
            {t('appointment_details') || 'Detalles de la Cita'}
          </h4>
          <div className="space-y-2 text-sm">
            <p>
              <span className="font-medium">{t('client') || 'Cliente'}:</span> {appointmentName}
            </p>
            <p>
              <span className="font-medium">{t('phone_number') || 'Teléfono'}:</span> {appointmentPhone}
            </p>
            {scheduledDate && (
              <p>
                <span className="font-medium">{t('scheduled_date') || 'Fecha Agendada'}:</span> {formatDate(scheduledDate)}
              </p>
            )}
          </div>
        </div>

        {/* Predefined reasons */}
        <div className="mb-4">
          <label className="label">
            <span className="label-text font-medium">
              {t('select_cancellation_reason') || 'Selecciona el motivo de cancelación'}
            </span>
          </label>
          <div className="space-y-2">
            {predefinedReasons.map((reason) => (
              <label key={reason.key} className="flex items-center gap-3 cursor-pointer hover:bg-base-200 p-2 rounded">
                <input
                  type="radio"
                  name="cancelReason"
                  value={reason.value}
                  checked={
                    reason.value === (t('other_reason') || 'Otro motivo') 
                      ? isOtherReason
                      : cancelReason === reason.value
                  }
                  onChange={() => handleReasonSelect(reason.value)}
                  className="radio radio-primary"
                />
                <span className="text-sm">{reason.value}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Custom reason input - only show when "Other reason" is selected */}
        {isOtherReason && (
          <div className="mb-6">
            <label className="label">
              <span className="label-text font-medium">
                {t('other_reason') || 'Otro motivo'}
              </span>
            </label>
            <input
              type="text"
              className="input input-bordered w-full focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
              placeholder={t('enter_additional_details') || 'Ingresa detalles adicionales sobre la cancelación...'}
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              maxLength={60}
            />
            <div className="text-xs text-gray-500 mt-1 text-right">
              {customReason.length}/60 caracteres
            </div>
          </div>
        )}

        {/* Warning message */}
        <div className="alert alert-warning mb-6">
          <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <span className="text-sm">
            {t('cancellation_warning') || 'Esta acción no se puede deshacer. El cliente será notificado de la cancelación.'}
          </span>
        </div>

        {/* Action buttons */}
        <div className="modal-action">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {t('cancel') || 'Cancelar'}
          </button>
          <button
            type="button"
            className="btn btn-error"
            onClick={handleConfirm}
            disabled={(!cancelReason.trim() && !customReason.trim()) || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="loading loading-spinner loading-sm mr-2"></span>
                {t('canceling') || 'Cancelando...'}
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                {t('confirm_cancellation') || 'Confirmar Cancelación'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CancelAppointmentDialog;
